import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { DataContainer } from 'components/DataContainer';
import { getLastSearchedVehicleKey, getPlateReferences } from 'domains/catalog/Catalog.store';
import { ReferenceCardIndex } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PartsSelection.styled';
import PlateReferenceCard from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PlateModal/PlateReferenceCard';
import { SelectedIndexType } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/SparePartsSection';
import { CenteredSpin, Flex, Text } from 'UI';
import { getData } from 'utils';
import { IndexType } from 'utils/svg/common';

interface PlateReferenceCardsContainerProps {
  plateId: string;
  selectedIndex: SelectedIndexType;
  setSelectedIndex: (value: ((prevState: IndexType) => IndexType) | IndexType) => void;
  setHighlightedIndex: (value: ((prevState: IndexType) => IndexType) | IndexType) => void;
}

const PlateReferenceCardsContainer = ({
  plateId,
  selectedIndex,
  setSelectedIndex,
  setHighlightedIndex,
}: PlateReferenceCardsContainerProps) => {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const plateReferences = useSelector((state: RootState) => getPlateReferences(state, { plateId, vehicleKey }));
  const plateReferencesData = getData(plateReferences);

  const isActive = (currentIndex: string | null, referenceIndex: string) => currentIndex === referenceIndex;

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!selectedIndex) {
      return;
    }
    itemRefs.current[selectedIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [selectedIndex, plateReferencesData]);

  return (
    <Flex direction={'column'} gap={10} overflowY={'scroll'} maxHeight={'70vh'} padding={'0 0 10px 0'}>
      <DataContainer
        data={plateReferences}
        Loading={() => (
          <Flex align={'center'} justify={'center'}>
            <CenteredSpin />
          </Flex>
        )}
      >
        {plateReferencesData?.map((reference) => (
          <Flex
            size={0}
            key={reference.index}
            gap={10}
            padding={'0 10px'}
            ref={(el) => (itemRefs.current[reference.index.toString()] = el)}
          >
            <ReferenceCardIndex
              minWidth={28}
              maxWidth={28}
              isActive={isActive(selectedIndex, reference.index.toString())}
              align={'flex-start'}
              justify={'center'}
              onClick={() => setSelectedIndex?.(reference.index.toString())}
              onMouseEnter={() => setHighlightedIndex?.(reference.index.toString())}
              onMouseLeave={() => setHighlightedIndex?.(null)}
            >
              <Text type="lg" bold color={isActive(selectedIndex, reference.index.toString()) ? 'white' : undefined}>
                {reference.index}
              </Text>
            </ReferenceCardIndex>
            <Flex direction={'column'} gap={10}>
              <PlateReferenceCard referenceNumber={reference.referenceNumber} plateId={plateId} />
              {reference.alternativeReferences?.map((altRef) => (
                <PlateReferenceCard key={altRef} referenceNumber={altRef} plateId={plateId} />
              ))}
            </Flex>
          </Flex>
        ))}
      </DataContainer>
    </Flex>
  );
};

export default PlateReferenceCardsContainer;
